import type { JSX } from 'react';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { diaDaSemanaEDia, horaCurta } from '../formato';
import { useEstreito } from '../ui';
import './painel.css';

/**
 * O molde de um painel de área: a cabeça e o arranjo (onda 318, RN-256).
 *
 * ── Duas perguntas, dois lados ─────────────────────────────────────────
 *
 * «Hoje» responde o que precisa de você agora (o resumo e a pilha);
 * «Andamento» responde como a área está indo no período (os indicadores).
 * Quem monta o painel entrega as duas metades prontas; aqui só se decide
 * COMO elas aparecem (decisão do Humberto, 08/10/2026, opção 3 do canvas):
 *
 *  · a partir de 1100 px, lado a lado — Hoje com ≈ 2/3 e Andamento com
 *    ≈ 1/3 —, sem clique: à esquerda o que fazer, à direita como estamos;
 *  · abaixo disso (tablet e celular), abas «Hoje | Andamento», para cada
 *    tela continuar curta. A aba fica no endereço (`?aba=`), como as abas
 *    do painel antigo: recarregar ou mandar o link mantém.
 *
 * O corte é perguntado ao navegador (`useEstreito(1099)`) e não só ao CSS
 * porque as duas formas têm marcação diferente: as abas são um
 * `tablist` de verdade, que o leitor de tela anuncia, e lado a lado não há
 * aba nenhuma. As duas metades ficam montadas nas duas formas (a de trás,
 * só escondida): trocar de aba não perde o filtro que a pessoa escolheu.
 *
 * O título de cada metade (`h2`) existe nas duas formas: lado a lado ele
 * aparece; em abas fica só para o leitor de tela, para os títulos não
 * pularem do `h1` direto ao `h3` dos cartões.
 *
 * `abaixo` é o que abre por baixo do Andamento quando alguém pede (no
 * LabManager, «Gráficos e máquinas»): lado a lado, ocupa a largura inteira,
 * embaixo das duas colunas; em abas, fica no fim da aba Andamento.
 */

type Aba = 'hoje' | 'andamento';

const ABAS: readonly { id: Aba; rotulo: string }[] = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: 'andamento', rotulo: 'Andamento' },
];

/**
 * A altura do que fica preso no topo do celular, onde as abas grudam: o
 * topo e, quando aparece, a faixa da rede (308). A `Moldura` a escreve em
 * `--altura-presa` no `<html>` (Ruling R32), e o CSS das abas usa a mesma
 * variável; sem ela, 48 px, o topo sozinho.
 */
function alturaPresa(): number {
  const lida = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--altura-presa'));
  return lida || 48;
}

export function PainelDoEspaco({ hoje, andamento, atualizadoEm, abaixo, titulo = 'Painel' }: {
  hoje: ReactNode;
  andamento: ReactNode;
  /** Quando os dados chegaram; `null` enquanto ainda não chegaram. */
  atualizadoEm: Date | null;
  /** O que abre por baixo do Andamento (largura inteira no PC). */
  abaixo?: ReactNode;
  /** O `h1` do painel: «Painel» no LabManager; o da área nos outros («Painel do Comercial», onda 552). */
  titulo?: string;
}): JSX.Element {
  const emAbas = useEstreito(1099);
  const [sp, setSp] = useSearchParams();
  const aba: Aba = sp.get('aba') === 'andamento' ? 'andamento' : 'hoje';
  const cabeca = useRef<HTMLElement>(null);
  const botoes = useRef<Record<Aba, HTMLButtonElement | null>>({ hoje: null, andamento: null });

  function trocar(nova: Aba, foco = false) {
    if (foco) botoes.current[nova]?.focus();
    if (nova === aba) return;
    const n = new URLSearchParams(sp);
    n.set('aba', nova);
    setSp(n, { replace: true });
    /* Com a pilha rolada, as abas estão grudadas embaixo do que estiver
       preso no topo; a outra metade começa logo abaixo delas, e não no meio
       dela — nem atrás delas, quando a faixa da rede está à vista. */
    const fim = cabeca.current?.getBoundingClientRect().bottom;
    if (fim !== undefined) {
      const onde = window.scrollY + fim - alturaPresa();
      if (window.scrollY > onde) window.scrollTo(0, onde);
    }
  }

  /* As setas, o Home e o End andam entre as abas (o padrão de abas do ARIA). */
  function teclar(e: KeyboardEvent<HTMLDivElement>) {
    const i = ABAS.findIndex((a) => a.id === aba);
    const para = e.key === 'ArrowRight' ? ABAS[(i + 1) % ABAS.length]
      : e.key === 'ArrowLeft' ? ABAS[(i + ABAS.length - 1) % ABAS.length]
      : e.key === 'Home' ? ABAS[0]
      : e.key === 'End' ? ABAS[ABAS.length - 1]
      : null;
    if (!para) return;
    e.preventDefault();
    trocar(para.id, true);
  }

  /* Uma data inválida não derruba o painel: o `toISOString()` dela lança
     exceção. A hora vira «—», e o dia é o de hoje. */
  const valida = atualizadoEm && !Number.isNaN(atualizadoEm.getTime()) ? atualizadoEm : null;
  const quando = valida ?? new Date();

  return (
    <div className="pn-painel">
      <header ref={cabeca} className="cabeca pn-cabeca">
        <h1>{titulo}</h1>
        <p className="pn-frase">O que precisa de você hoje</p>
        <p className="pn-quando">
          {/* O dia e a hora pelo formato.ts, no fuso da casa (P-FMT-01):
              «quinta, 08/10 · atualizado às 07:42». */}
          <span>{diaDaSemanaEDia(quando)}</span>
          {valida && (
            <> · atualizado às <time dateTime={valida.toISOString()}>{horaCurta(valida)}</time></>
          )}
          {atualizadoEm && !valida && <> · atualizado às —</>}
        </p>
      </header>

      {emAbas && (
        <div role="tablist" aria-label="Partes do painel" className="pn-abas" onKeyDown={teclar}>
          {ABAS.map((a) => (
            <button
              key={a.id}
              ref={(el) => { botoes.current[a.id] = el; }}
              type="button"
              role="tab"
              id={`pn-aba-${a.id}`}
              aria-selected={a.id === aba}
              aria-controls={`pn-parte-${a.id}`}
              tabIndex={a.id === aba ? 0 : -1}
              className="pn-aba"
              onClick={() => trocar(a.id)}
            >
              {a.rotulo}
            </button>
          ))}
        </div>
      )}

      <div className={emAbas ? 'pn-em-abas' : 'pn-lado-a-lado'}>
        {ABAS.map((a) => (
          <section
            key={a.id}
            id={`pn-parte-${a.id}`}
            className={`pn-parte pn-parte-${a.id}`}
            role={emAbas ? 'tabpanel' : undefined}
            aria-labelledby={emAbas ? `pn-aba-${a.id}` : `pn-titulo-${a.id}`}
            hidden={emAbas && a.id !== aba}
          >
            <h2 id={`pn-titulo-${a.id}`} className={emAbas ? 'pn-parte-titulo só-leitor' : 'pn-parte-titulo'}>
              {a.rotulo}
            </h2>
            {a.id === 'hoje' ? hoje : andamento}
            {emAbas && a.id === 'andamento' && abaixo}
          </section>
        ))}
      </div>
      {!emAbas && abaixo}
    </div>
  );
}
